import React, { FC, useEffect, useReducer } from 'react';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks';
import { action } from 'typesafe-actions';
import { currencyService, exchangeCalculateService } from '../../../../../channels/secure-web/services';
import { AppLoader } from '../../../../content-composer/src/components/Generic/AppLoader';
import { Currency } from '../../domain/Currency/entities';
import { AmountRateResponseDTO } from '../../domain/ExchangeCalculate/entities';
import { ExchangeCalculateContext, ExchangeCalculateDispatchContext } from './ExchangeCalculateContext';
import { defaultExchangeCalculateState } from './defaultState';
import { ExchangeCalculateReducer } from './ExchangeCalculateReducer';
import { ExchangeCalculateContextActionTypes } from './types';

const EURO = 'EUR';
const USD = 'USD';

const ExchangeCalculateProvider: FC = ({ children }) => {
  // Get initial state values
  const [state, dispatch] = useReducer(ExchangeCalculateReducer, defaultExchangeCalculateState);

  // Set the services and its implementation on provider mount
  // Define api call conditions
  const shouldFetchCurrency = Boolean(currencyService);
  const shouldCalculateExchange = Boolean(state.exchangeCalculateRequest);

  // Define api calls
  // Getting all the currencies (each currency's serialNumber, description, shortDescription)
  const {
    data: currenciesData,
    error: currenciesError,
    isLoading: isLoadingCurrencies,
  } = useInvokeSWRService<Currency[]>(
    currencyService.GetCurrencies,
    {},
    shouldFetchCurrency,
    'currencyService.GetCurrencies',
  );

  // Posting {fromCurrencyId, toCurrencyId, amount} and getting back {amount, rate}.
  const {
    data: amountRateData,
    error: amountRateError,
    isLoading: isLoadingAmountRate,
  } = useInvokeSWRService<AmountRateResponseDTO>(
    exchangeCalculateService.PostExchangeCalculate,
    { data: state.exchangeCalculateRequest },
    shouldCalculateExchange,
    'exchangeCalculateService.PostExchangeCalculate',
  );

  // Default EUR to USD conversion when first loading the page
  useEffect(() => {
    if (state.isFirstExchangeCalculation) {
      const fromCurrencyId = currenciesData?.find(currency => currency.shortDescription === EURO)?.serialNumber;
      const toCurrencyId = currenciesData?.find(currency => currency.shortDescription === USD)?.serialNumber;
      if (fromCurrencyId && toCurrencyId) {
        onExchangeCalculate(fromCurrencyId, toCurrencyId, 1, false);
      }
    }
  }, [currenciesData, state.isFirstExchangeCalculation]);

  // Setting the exchangeCalculateRequest { fromCurrencyId, toCurrencyId, amount} which is
  // the payload for the PostExchangeCalculate
  const onExchangeCalculate = (
    fromCurrencyId: number,
    toCurrencyId: number,
    amount?: number,
    isFirstExchangeCalculation?: boolean,
  ) => {
    dispatch(
      action(ExchangeCalculateContextActionTypes.SetExchangeCalculateRequest, {
        fromCurrencyId,
        toCurrencyId,
        amount,
        isFirstExchangeCalculation,
      }),
    );
  };

  return (
    <ExchangeCalculateContext.Provider
      value={{
        ...state,
        currenciesData: currenciesData ?? null,
        currenciesError,
        isLoadingCurrencies,
        amountRateData: amountRateData ?? null,
        amountRateError,
        isLoadingAmountRate,
        onExchangeCalculate,
      }}>
      <ExchangeCalculateDispatchContext.Provider value={dispatch}>
        {currenciesData && children}
        {isLoadingCurrencies && <AppLoader />}
      </ExchangeCalculateDispatchContext.Provider>
    </ExchangeCalculateContext.Provider>
  );
};

export { ExchangeCalculateProvider };
